/**
 * Display formatting helpers (pure, no side effects).
 */

/** Milliseconds → "m:ss" (or "h:mm:ss" for long calls). Used by the player. */
export function msToClock(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    const mm = String(minutes).padStart(2, "0");
    return `${hours}:${mm}:${ss}`;
  }
  return `${minutes}:${ss}`;
}

/** Seconds (RingCentral duration_sec) → "m:ss". */
export function secToClock(sec: number): string {
  return msToClock(sec * 1000);
}

export type ScoreBand = "strong" | "solid" | "fair" | "weak";

/**
 * Map a 0–100 score to a band. Keep thresholds in sync with the --band-* CSS
 * tokens in index.css and the ScoreBadge component.
 *   85–100 strong (exemplary) · 70–84 solid · 50–69 fair (needs work) · 0–49 weak
 */
export function scoreBand(total: number): ScoreBand {
  if (total >= 85) return "strong";
  if (total >= 70) return "solid";
  if (total >= 50) return "fair";
  return "weak";
}

const BAND_LABELS: Record<ScoreBand, string> = {
  strong: "Exemplary",
  solid: "Solid",
  fair: "Needs work",
  weak: "Weak",
};

export function scoreBandLabel(total: number): string {
  return BAND_LABELS[scoreBand(total)];
}

/** Tailwind text/bg class pairs for a band (used by ScoreBadge). */
export function scoreBandClasses(total: number): string {
  switch (scoreBand(total)) {
    case "strong":
      return "bg-band-strong/15 text-band-strong";
    case "solid":
      return "bg-band-solid/15 text-band-solid";
    case "fair":
      return "bg-band-fair/15 text-band-fair";
    case "weak":
      return "bg-band-weak/15 text-band-weak";
  }
}

/** ISO timestamp → short local date/time for list rows. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Signed delta string for the self-vs-AI gap ("+4", "-3", "0"). */
export function formatDelta(delta: number): string {
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

/** Cents → "$12.34" (money is stored in cents — admin cost surfaces only). */
export function centsToDollars(cents: number): string {
  const v = (Number.isFinite(cents) ? cents : 0) / 100;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** Token count → compact display ("932", "12.4k", "1.2M"). */
export function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}
